import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Form, type FormikProps } from 'formik';
import { Text } from '@rsos/base-components';
import { Button } from '@rsos/components/capstone/base';
import {
  useQueuedCallerIDs,
  useSelectedCallerID,
} from '@rsos/reduxDataStore/src/features/emergencyCalls';
import {
  clearPreselectedTrain,
  usePreselectedNorfolkSouthernTrain,
  type NorfolkSouthernTrainSelection,
} from '@rsos/reduxDataStore/src/features/norfolkSouthern/norfolkSouthernSlice';
import {
  closeResourcePanel,
  markTransportSeen,
  setResourcePanelView,
  setSelectedRequest,
  useBreadCrumbs,
  useCreateNorfolkSouthernRequestMutation,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  originDateFromTrainId,
  useNorfolkSouthernTrains,
} from '../../../../../utils/norfolkSouthern/trains';
import TowProFormSection from '../TowPro/TowProFormSection';
import FieldLabel from './FieldLabel';
import styles from './NorfolkSouthernForm.module.css';
import OriginDateField from './OriginDateField';
import ReasonField from './ReasonField';
import RelatedCallField from './RelatedCallField';
import TrainField from './TrainField';
import {
  NORFOLK_SOUTHERN_INITIAL_FORM_VALUES,
  buildConsistRequestBody,
  getConsistSubmitErrorMessage,
  validateNorfolkSouthernForm,
  type NorfolkSouthernFormValues,
} from './norfolkSouthernFormUtils';

const TRAIN_LEFT_MAP_MESSAGE =
  'That train is no longer on the map. Select another train.';

const SUBMIT_NOTICE =
  'Submitting request will notify Norfolk Southern and first responders enrolled in notifications. Submit only for an actual, current emergency.';

interface SegmentedFieldProps<Value> {
  label: string;
  options: { label: string; value: Value }[];
  value: Value;
  onChange: (value: Value) => void;
  disabled: boolean;
  dataNamePrefix: string;
}

/** Two-way choice rendered as adjoining buttons, e.g. No / Yes. */
function SegmentedField<Value extends string | boolean>({
  label,
  options,
  value,
  onChange,
  disabled,
  dataNamePrefix,
}: SegmentedFieldProps<Value>) {
  return (
    <div className={styles.field}>
      <FieldLabel>{label}</FieldLabel>
      <div className={styles.segmented} role="radiogroup" aria-label={label}>
        {options.map(option => {
          const selected = option.value === value;
          return (
            <button
              key={String(option.value)}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              className={`${styles.segment}${selected ? ` ${styles.segmentSelected}` : ''}`}
              onClick={() => onChange(option.value)}
              data-name={`${dataNamePrefix}-${option.label.toLowerCase()}`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Consist request form. Opened from a train's map card with that train
 * preselected, or from the Resource Request Center's Train Consist option,
 * where the train field lists the trains currently in the jurisdiction. A
 * train is required either way: the backend refuses a request without a
 * map-selected train.
 */
function NorfolkSouthernForm(): JSX.Element {
  const dispatch = useDispatch();
  const breadCrumbs = useBreadCrumbs();
  const selectedCallerID = useSelectedCallerID();
  const preselectedTrain = usePreselectedNorfolkSouthernTrain();
  const trainsOnMap = useNorfolkSouthernTrains();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pickedTrain, setPickedTrain] =
    useState<NorfolkSouthernTrainSelection | null>(null);
  const [createRequest, { isLoading: isSubmitting }] =
    useCreateNorfolkSouthernRequestMutation();
  const formRef = useRef<FormikProps<NorfolkSouthernFormValues>>(null);
  const queuedCallerIds = useQueuedCallerIDs();

  // The trains offered by the picker: everything on the map, plus any train
  // the user already chose (from the map card or the picker) that has since
  // left the map or the jurisdiction. A chosen train stays selectable because
  // the request carries its snapshot, and the backend decides whether the
  // position is still covered.
  const trains = useMemo((): NorfolkSouthernTrainSelection[] => {
    const isOnMap = (train: NorfolkSouthernTrainSelection): boolean =>
      trainsOnMap.some(onMap => onMap.externalId === train.externalId);

    const pinned: NorfolkSouthernTrainSelection[] = [];
    for (const chosen of [preselectedTrain, pickedTrain]) {
      if (!chosen || isOnMap(chosen)) {
        continue;
      }
      const alreadyPinned = pinned.some(
        train => train.externalId === chosen.externalId,
      );
      if (!alreadyPinned) {
        pinned.push(chosen);
      }
    }
    return [...pinned, ...trainsOnMap];
  }, [preselectedTrain, pickedTrain, trainsOnMap]);

  const preselectedExternalId = preselectedTrain?.externalId ?? '';
  const preselectedTrainId = preselectedTrain?.trainId ?? '';
  const initialValues = useMemo(
    (): NorfolkSouthernFormValues => ({
      ...NORFOLK_SOUTHERN_INITIAL_FORM_VALUES,
      externalId: preselectedExternalId,
      originDate: preselectedTrainId
        ? originDateFromTrainId(preselectedTrainId)
        : '',
      relatedCall: selectedCallerID ?? '',
    }),
    // Choosing another train on the map while the form is open restarts the
    // form for that train. Call selection changes are merged into the
    // related call below instead, since reinitializing would wipe what the
    // user entered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preselectedExternalId],
  );

  // Switching calls replaces the related call with the new call's, leaving
  // the rest of the form as the user left it.
  const lastSelectedCallerID = useRef(selectedCallerID);
  useEffect(() => {
    if (lastSelectedCallerID.current === selectedCallerID) {
      return;
    }
    lastSelectedCallerID.current = selectedCallerID;
    formRef.current?.setFieldValue('relatedCall', selectedCallerID ?? '', true);
  }, [selectedCallerID]);

  // The preselection belongs to this form instance only.
  useEffect(
    () => () => {
      dispatch(clearPreselectedTrain());
    },
    [dispatch],
  );

  const handleCancel = (): void => {
    if (breadCrumbs.includes('list_requests') && breadCrumbs.length > 1) {
      dispatch(setResourcePanelView('list_requests'));
    } else {
      dispatch(closeResourcePanel());
    }
  };

  const handleSubmit = async (
    values: NorfolkSouthernFormValues,
  ): Promise<void> => {
    const train = trains.find(t => t.externalId === values.externalId);
    if (!train) {
      setSubmitError(TRAIN_LEFT_MAP_MESSAGE);
      return;
    }

    setSubmitError(null);
    try {
      const created = await createRequest(
        buildConsistRequestBody(train, values),
      ).unwrap();
      dispatch(markTransportSeen(String(created.id)));
      dispatch(
        setSelectedRequest({
          requestId: String(created.id),
          detailView: 'norfolk_southern',
        }),
      );
    } catch (error) {
      setSubmitError(getConsistSubmitErrorMessage(error));
    }
  };

  const hasBlockingErrors = (values: NorfolkSouthernFormValues): boolean =>
    Object.keys(validateNorfolkSouthernForm(values)).length > 0;

  return (
    <div className={styles.form} data-name="norfolk-southern-form">
      <div className={styles.title}>
        <Text color="primaryText" size="large" fontWeight="bold">
          New Train Consist Request
        </Text>
      </div>

      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        validate={validateNorfolkSouthernForm}
        validateOnMount
        validateOnChange={false}
        validateOnBlur
        onSubmit={handleSubmit}
      >
        {formik => (
          <Form id="norfolkSouthernForm">
            <TowProFormSection
              title="Request"
              dataNamePrefix="norfolk-southern"
            >
              <TrainField
                formik={formik}
                trains={trains}
                onPickTrain={setPickedTrain}
              />
              <OriginDateField formik={formik} />
              <ReasonField formik={formik} />

              <SegmentedField
                label="Foreign Train"
                options={[
                  { label: 'No', value: false },
                  { label: 'Yes', value: true },
                ]}
                value={formik.values.foreignTrain}
                onChange={foreignTrain =>
                  formik.setFieldValue('foreignTrain', foreignTrain)
                }
                disabled={isSubmitting}
                dataNamePrefix="norfolk-southern-foreign-train"
              />

              <RelatedCallField formik={formik} callerIds={queuedCallerIds} />
            </TowProFormSection>

            {submitError && (
              <div className={styles.errorBanner} role="alert">
                <Text color="background" size="small">
                  {submitError}
                </Text>
              </div>
            )}

            <div
              className={styles.footerNote}
              data-name="norfolk-southern-submit-notice"
            >
              {SUBMIT_NOTICE}
            </div>

            <div className={styles.buttons}>
              <Button
                grow
                btnType="tertiary"
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting}
                mx={0}
                dataName="norfolk-southern-form-cancel-button"
              >
                Cancel
              </Button>
              <Button
                grow
                type="submit"
                btnType="primary"
                disabled={isSubmitting || hasBlockingErrors(formik.values)}
                dataName="norfolk-southern-form-submit-button"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default NorfolkSouthernForm;
